import React, { useEffect, useState } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";

const CONFIRM_WORD = "YehBaby"; // exact, case-sensitive
const YEH_BABY_GIF =
  "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExMHFsbzZ5a2g2Zm0xdGkwYmZjemJvdXZkaDhzOXQ2bzZsMDk3bW90bSZlcD12MV9naWZzX3NlYXJjaCZjdD1n/xUPGcJ9uOAL2h5wA5a/giphy.gif";

/**
 * Dispense-type migration prep. Pick one chemist, then back up + wipe its
 * customers' DispenseCustomerIDs (OldDispenseCustomerID := DispenseCustomerID,
 * verify, then DispenseCustomerID := NULL) via a single transactional endpoint.
 *
 * Stages: "select" → "confirm" (type YehBaby) → "done".
 */
const DispenseMigrationModal = ({ isOpen, onClose }) => {
  const [chemists, setChemists] = useState([]);
  const [isFetchingChemists, setIsFetchingChemists] = useState(false);
  const [selectedChemist, setSelectedChemist] = useState(null);

  const [stage, setStage] = useState("select"); // select | confirm | done
  const [confirmText, setConfirmText] = useState("");
  const [isMigrating, setIsMigrating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { backedUp, wiped }

  useEffect(() => {
    if (isOpen) {
      resetState();
      fetchChemists();
    }
  }, [isOpen]);

  const resetState = () => {
    setSelectedChemist(null);
    setStage("select");
    setConfirmText("");
    setIsMigrating(false);
    setError(null);
    setResult(null);
  };

  const fetchChemists = async () => {
    setIsFetchingChemists(true);
    try {
      const response = await phappsRequest(API_PATHS.CHEMIST_LIST, "GET");
      if (response.success && Array.isArray(response.chemists)) {
        setChemists(response.chemists);
      } else {
        setError("Failed to load chemists list");
      }
    } catch (e) {
      console.error(e);
      setError("Error loading chemists");
    } finally {
      setIsFetchingChemists(false);
    }
  };

  const handleMigrate = async () => {
    if (!selectedChemist || confirmText !== CONFIRM_WORD) return;
    setIsMigrating(true);
    setError(null);
    try {
      const response = await phappsRequest(
        API_PATHS.ADMIN_BACKUP_WIPE_DISPENSE_CUSTOMER_IDS,
        "POST",
        { ChemistID: selectedChemist.chemistID }
      );
      if (response.success) {
        setResult({ backedUp: response.backedUp, wiped: response.wiped });
        setStage("done");
      } else {
        setError(response.message || "Migration failed");
      }
    } catch (e) {
      // 409 = already-wiped guard, or any other server error
      setError(
        e?.response?.data?.message || e?.message || "Migration failed"
      );
    } finally {
      setIsMigrating(false);
    }
  };

  if (!isOpen) return null;

  const confirmMatches = confirmText === CONFIRM_WORD;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        {/* ── Stage: DONE ─────────────────────────────────────────────── */}
        {stage === "done" ? (
          <div className="text-center">
            <h2 className="text-xl font-bold mb-1 text-green-700">Completed</h2>
            <p className="text-sm text-gray-600 mb-4">
              Dispense Customer IDs backed up and wiped for{" "}
              <span className="font-semibold">
                {selectedChemist?.chemistName}
              </span>
              .
            </p>
            <img
              src={YEH_BABY_GIF}
              alt="Yeh, baby!"
              className="rounded-lg mx-auto mb-4 max-h-56 w-auto"
            />
            <div className="flex justify-center gap-6 text-sm text-gray-700 mb-5">
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {result?.backedUp ?? "—"}
                </div>
                <div className="text-xs text-gray-500">Backed up</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {result?.wiped ?? "—"}
                </div>
                <div className="text-xs text-gray-500">Wiped</div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 w-full"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold mb-1">Dispense Migration</h2>
            <p className="text-sm text-gray-500 mb-4">
              Back up and wipe Dispense Customer IDs for a single chemist ahead
              of a dispense-type migration.
            </p>

            {/* ── Chemist picker ── */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chemist
              </label>
              <Autocomplete
                options={chemists}
                getOptionLabel={(o) => `${o.chemistName} (ID: ${o.chemistID})`}
                value={selectedChemist}
                disabled={stage === "confirm" || isMigrating}
                onChange={(_e, v) => {
                  setSelectedChemist(v);
                  setError(null);
                }}
                loading={isFetchingChemists}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search chemist..."
                    variant="outlined"
                    size="small"
                  />
                )}
                style={{ width: "100%" }}
              />
            </div>

            {/* ── Stage: CONFIRM ── */}
            {stage === "confirm" && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2 mb-3">
                  <WarningAmberOutlinedIcon
                    sx={{ color: "#d97706", fontSize: 20, mt: "1px" }}
                  />
                  <p className="text-sm text-amber-800">
                    This will copy every{" "}
                    <span className="font-mono">DispenseCustomerID</span> into{" "}
                    <span className="font-mono">OldDispenseCustomerID</span> for{" "}
                    <span className="font-semibold">
                      {selectedChemist?.chemistName}
                    </span>
                    , then set all{" "}
                    <span className="font-mono">DispenseCustomerID</span> to
                    NULL. This cannot be undone from here.
                  </p>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type{" "}
                  <span className="font-mono font-bold">{CONFIRM_WORD}</span> to
                  confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  autoFocus
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 border p-2 font-mono"
                  placeholder={CONFIRM_WORD}
                />
              </div>
            )}

            {/* ── Error ── */}
            {error && (
              <div className="p-3 rounded-lg bg-red-100 text-red-700 text-sm mb-4">
                {error}
              </div>
            )}

            {/* ── Actions ── */}
            <div className="flex justify-end gap-3 mt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isMigrating}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>

              {stage === "select" ? (
                <button
                  type="button"
                  disabled={!selectedChemist}
                  onClick={() => {
                    setError(null);
                    setConfirmText("");
                    setStage("confirm");
                  }}
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <BackupOutlinedIcon sx={{ fontSize: 18 }} />
                  Backup and wipe Dispense Customer IDs
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!confirmMatches || isMigrating}
                  onClick={handleMigrate}
                  className="px-5 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {isMigrating && (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  )}
                  {isMigrating ? "Migrating..." : "Migrate"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DispenseMigrationModal;
